import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Notification, toast, Dialog } from 'components/ui'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const toNumberOrNull = (value) => {
    if (value === null || value === undefined || value === '') return null
    const num = Number(value)
    return Number.isFinite(num) ? num : null
}

const normalizeLatLng = (latitude, longitude) => {
    const lat = toNumberOrNull(latitude)
    const lng = toNumberOrNull(longitude)
    if (lat === null || lng === null) return null
    if (lat < -90 || lat > 90) return null
    if (lng < -180 || lng > 180) return null
    return { lat, lng }
}

const GoogleMapsRedirectButton = ({
    latitude,
    longitude,
    farmers = [],
    facilitators = [],
    label = 'View Location',
    primaryLabel = 'F',
    onBeforeOpen,
    onFacilitatorClick,
    ...buttonProps
}) => {
    const [open, setOpen] = useState(false)
    const mapDivRef = useRef(null)
    const mapRef = useRef(null)
    const [tileError, setTileError] = useState(false)
    const [staticUrl, setStaticUrl] = useState(null)

    const single = useMemo(() => normalizeLatLng(latitude, longitude), [latitude, longitude])

    const farmerPoints = useMemo(() => {
        const arr = [...(farmers || [])]
        if (!arr.length && single) {
            arr.push({ latitude: single.lat, longitude: single.lng, label: primaryLabel })
        }
        return arr
            .map((p) => {
                const n = normalizeLatLng(p?.latitude, p?.longitude)
                return n ? { ...n, label: p?.label || 'F' } : null
            })
            .filter(Boolean)
            .map((p) => ({ lat: p.lat, lng: p.lng, label: p.label }))
    }, [farmers, single, primaryLabel])

    const facilitatorPoints = useMemo(() => {
        return [...(facilitators || [])]
            .map((p) => {
                const n = normalizeLatLng(p?.latitude, p?.longitude)
                return n ? { lat: n.lat, lng: n.lng, label: 'I', originalData: p } : null
            })
            .filter(Boolean)
    }, [facilitators])

    const hasAny = farmerPoints.length > 0 || facilitatorPoints.length > 0

    const handleClick = async () => {
        try {
            console.log('GMRB/click', {
                farmersCount: farmerPoints.length,
                facilitatorsCount: facilitatorPoints.length,
                hasAny,
            })
        } catch {}
        if (!hasAny) {
            toast.push(
                <Notification title={'Error'} type="danger" duration={2500}>
                    Invalid coordinates
                </Notification>,
                { placement: 'bottom-end' }
            )
            return
        }
        try {
            if (typeof onBeforeOpen === 'function') {
                console.log('GMRB/beforeOpen:start')
                const r = onBeforeOpen()
                if (r && typeof r.then === 'function') {
                    await r
                }
                console.log('GMRB/beforeOpen:done', {
                    farmersCount: farmerPoints.length,
                    facilitatorsCount: facilitatorPoints.length,
                })
            }
        } catch {}
        setOpen(true)
        try {
            console.log('GMRB/open:set', { open: true })
        } catch {}
    }

    const ensureLeafletCss = () => {
        const existing = document.querySelector('link[data-leaflet-css]')
        if (!existing) {
            const link = document.createElement('link')
            link.rel = 'stylesheet'
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
            link.setAttribute('data-leaflet-css', 'true')
            document.head.appendChild(link)
            try {
                console.log('GMRB/css:injected')
            } catch {}
        }
    }

    const waitForVisibleContainer = () =>
        new Promise((resolve) => {
            let attempts = 0
            const maxAttempts = 20
            const tick = () => {
                attempts += 1
                const el = mapDivRef.current
                if (el) {
                    const rect = el.getBoundingClientRect()
                    if (rect.width > 0 && rect.height > 0) {
                        try {
                            console.log('GMRB/container:visible', { width: rect.width, height: rect.height })
                        } catch {}
                        resolve(true)
                        return
                    }
                }
                if (attempts >= maxAttempts) {
                    try {
                        console.warn('GMRB/container:timeout')
                    } catch {}
                    resolve(true)
                    return
                }
                setTimeout(tick, 100)
            }
            tick()
        })

    useEffect(() => {
        const initMap = async () => {
            if (!open) return
            ensureLeafletCss()
            await waitForVisibleContainer()

            if (!mapDivRef.current) return
            const center = farmerPoints[0] || facilitatorPoints[0] || { lat: 0, lng: 0 }
            const map = L.map(mapDivRef.current, {
                center: [center.lat, center.lng],
                zoom: 13,
            })
            mapRef.current = map
            try {
                console.log('GMRB/map:init', { center })
            } catch {}

            const layer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                maxZoom: 19,
            })
            layer.on('tileerror', () => {
                try {
                    console.warn('GMRB/tile:error')
                } catch {}
                setTileError(true)
            })
            layer.addTo(map)

            const bounds = L.latLngBounds([])

            ;[...farmerPoints, ...facilitatorPoints].forEach((p) => {
                const bg = p.label === 'F' ? 'background-color:#dc2626' : p.label === 'I' ? 'background-color:#2563eb' : 'background-color:#059669'
                const icon = L.divIcon({
                    className: 'leaflet-div-icon',
                    html: `<div style="width:24px;height:24px;border-radius:12px;color:#fff;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 0 4px rgba(0,0,0,0.3);${bg}">${p.label}</div>`,
                    iconSize: [24, 24],
                    iconAnchor: [12, 12],
                })
                const marker = L.marker([p.lat, p.lng], { icon }).addTo(map)
                if (p.label === 'I' && typeof onFacilitatorClick === 'function') {
                    marker.on('click', () => {
                        onFacilitatorClick(p.originalData)
                    })
                }
                bounds.extend([p.lat, p.lng])
                try {
                    console.log('GMRB/marker:add', { label: p.label, lat: p.lat, lng: p.lng })
                } catch {}
            })

            if (farmerPoints.length + facilitatorPoints.length > 0) {
                map.fitBounds(bounds, { padding: [20, 20] })
                setTimeout(() => {
                    map.invalidateSize()
                }, 200)
                setTimeout(() => {
                    map.invalidateSize()
                }, 800)
                try {
                    console.log('GMRB/map:fitBounds', {
                        count: farmerPoints.length + facilitatorPoints.length,
                    })
                } catch {}
            }
        }
        initMap()
    }, [open, farmerPoints, facilitatorPoints, onFacilitatorClick])

    useEffect(() => {
        return () => {
            if (mapRef.current) {
                try {
                    mapRef.current.remove()
                } catch {}
                mapRef.current = null
                try {
                    console.log('GMRB/map:removed')
                } catch {}
            }
        }
    }, [open])

    useEffect(() => {
        const buildStaticUrl = () => {
            const all = [...farmerPoints, ...facilitatorPoints]
            if (all.length === 0) return null
            const c = farmerPoints[0] || facilitatorPoints[0]
            const base = `https://staticmap.openstreetmap.de/staticmap.php?center=${encodeURIComponent(c.lat)},${encodeURIComponent(c.lng)}&zoom=14&size=600x320`
            const markers = all
                .map((p) => {
                    const color = p.label === 'F' ? 'red-pushpin' : p.label === 'I' ? 'blue-pushpin' : 'green-pushpin'
                    return `&markers=${encodeURIComponent(`${p.lat},${p.lng},${color}`)}`
                })
                .join('')
            return `${base}${markers}`
        }
        setStaticUrl(buildStaticUrl())
    }, [farmerPoints, facilitatorPoints])

    return (
        <>
            <Button
                size="sm"
                variant="solid"
                {...buttonProps}
                disabled={buttonProps.disabled ?? !hasAny}
                onClick={handleClick}
            >
                {label}
            </Button>
            <Dialog isOpen={open} onClose={() => setOpen(false)} contentClassName="p-0">
                <div className="px-6 pt-5 pb-3 border-b">
                    <h4 className="text-blue-700 font-semibold">View Location</h4>
                </div>
                <div className="px-6 py-4">
                    {tileError && staticUrl ? (
                        <img
                            src={staticUrl}
                            alt="Map"
                            className="w-full h-[320px] object-cover rounded"
                            onError={(e) => {
                                e.currentTarget.src = `https://tile.openstreetmap.org/2/1/1.png`
                            }}
                        />
                    ) : (
                        <div
                            ref={mapDivRef}
                            className="bg-gray-200 rounded"
                            style={{ width: '100%', height: 320 }}
                        />
                    )}
                </div>
                <div className="px-6 py-3 bg-gray-100 text-right">
                    <Button size="sm" onClick={() => setOpen(false)}>Close</Button>
                </div>
            </Dialog>
        </>
    )
}

export default GoogleMapsRedirectButton
